import { isAllowedAccountId } from '../config/accounts'
import { generateRole, type Role } from '../types/Role'

const authorizationApiBaseUrl = import.meta.env.VITE_AUTHORIZATION_API_URL ?? 'http://localhost:8082'

// ponytail: hardcoded to the same AWS SSO connection name cdh-data-portal-internal's
// AuthorizationApi.startDeviceFlow('secure') uses against this same authorizationApiUrl instance.
// Revisit if ops-hub ever gets its own dedicated SSO connection.
const DEVICE_FLOW_IDP = 'global'

export interface DeviceFlowStartResponse {
  device_code: string
  expires_in: number
  interval: number
  message: string
  user_code: string
  verification_uri: string
  verification_uri_complete: string
}

export interface DeviceFlowCompleteResponse {
  access_token?: string
  expires_in?: number
  message: string
}

export interface RefreshAccessTokenResponse {
  access_token: string
  expires_in: number
  message: string
}

interface SyncedAccount {
  account_id: string
  account_name: string
  email: string
  role_name: string
}

interface ListAccountsResponse {
  accounts: SyncedAccount[]
}

/** Thrown by fetchSSORoles when the SSO access token has expired (HTTP 401). */
export class UnauthorizedError extends Error {
  constructor() {
    super('SSO access token expired or invalid')
    this.name = 'UnauthorizedError'
  }
}

async function authorizationFetch<T>(path: string, body?: unknown): Promise<T> {
  let response: Response

  try {
    response = await fetch(`${authorizationApiBaseUrl}${path}`, {
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      method: 'POST',
    })
  } catch {
    throw new Error('Could not reach the authorization service')
  }

  if (response.status === 401) {
    throw new UnauthorizedError()
  }

  if (!response.ok) {
    throw new Error('Could not complete the authorization request')
  }

  return (await response.json()) as T
}

/**
 * Starts the AWS SSO OAuth Device Authorization Grant flow against the Authorization API,
 * mirroring cdh-data-portal-internal's AuthorizationApi.startDeviceFlow(). The caller must
 * already have an authenticated session cookie (see api/authenticationApi.ts).
 */
export function startDeviceFlow(): Promise<DeviceFlowStartResponse> {
  return authorizationFetch(`/aws-sso/start-device-flow/${DEVICE_FLOW_IDP}`)
}

/**
 * Polls whether the user has approved the device code shown by startDeviceFlow(). Resolves with
 * an empty `access_token` while the user hasn't approved it yet -- only a non-2xx response (or a
 * network failure) is treated as an actual error, matching cdh-data-portal-internal's
 * AuthorizationApi.completeDeviceFlow().
 */
export function completeDeviceFlow(deviceCode: string): Promise<DeviceFlowCompleteResponse> {
  return authorizationFetch('/aws-sso/complete-device-flow', { device_code: deviceCode })
}

/** Exchanges an expired SSO access token for a new one, without repeating the device flow. */
export function refreshSSOAccessToken(): Promise<RefreshAccessTokenResponse> {
  return authorizationFetch('/aws-sso/refresh-access-token')
}

/**
 * Fetches the AWS accounts/roles available to the given SSO access token and turns them into
 * ops-hub Roles, mirroring cdh-data-portal-internal's AuthorizationApi.getListAccounts() +
 * roles.ts's ssoRoles mapping (a synthetic `arn:aws:iam::{account_id}:role/{role_name}` per
 * account). Only ops-hub's own dev/prod accounts are valid role sources -- discard everything
 * else (e.g. dataset/hub accounts from other CDH apps sharing the same authorization service).
 */
export async function fetchSSORoles(accessToken: string): Promise<Role[]> {
  const { accounts } = await authorizationFetch<ListAccountsResponse>('/aws-sso/list-accounts', {
    access_token: accessToken,
  })

  return accounts
    .map((account) =>
      generateRole({ principalArn: '', roleArn: `arn:aws:iam::${account.account_id}:role/${account.role_name}` }),
    )
    .filter((role) => isAllowedAccountId(role.accountId))
}
